
import { Component, Mixins, Watch } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'
import ProjectMixin from '@/_mixins/project'
import ObservationMixin from '@/_mixins/observation'

import { Debounce } from 'lodash-decorators'

import ToolbarComponent from '@/_components/bar/appToolbar/appToolbar.vue'
import FilterPanel from '@/views/Project/Observation/components/filters/filterPanel.vue'
import TableView from '@/views/Project/Observation/views/TableView.vue'
import GridView from '@/views/Project/Observation/views/GridView.vue'
import Card from '@/views/Project/Observation/components/card.vue'

import ExportBtn from '@/views/Project/Observation/components/exportBtn/exportBtn.vue'

import AddBtnComponent from '@/components/AddBtn/AddBtn.vue'
import EditorViewer from '@/components/EditorViewer/EditorViewer.vue'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import VbitStatusBanner from '@/components/banner/vbitStatus.vue'

import { getRequest } from '@/plugins/api-axios'
import { deserialise } from 'kitsu-core'
import { filterToParams } from '@/types/observation'
@Component({
    components: {
        ToolbarComponent,
        FilterPanel,
        TableView,
        GridView,
        Card,
        AddBtnComponent,
        EditorViewer,
        ExportBtn,
        RichTextField,
        VbitStatusBanner,
    },
    mixins: [ProjectMixin, ObservationMixin],
})
export default class ObservationsList extends Mixins<MixinInterface>(
    ProjectMixin,
    ObservationMixin
) {
    projectUrl = process.env.VUE_APP_PROJECT_BASE_URL

    totalObservations = 0

    get meMode() {
        return this.$store.getters.meMode
    }

    get levelType() {
        return this.$route.params.type == 'g' ? 'projectGroup' : 'project'
    }

    get project() {
        return this.$route.params.type == 'p'
            ? this.$store.getters.project(this.$route.params.id)
            : null
    }

    get group() {
        return this.$route.params.type == 'g'
            ? this.$store.getters.group(this.$route.params.id)
            : null
    }

    get hasObservationTemplate() {
        return this.$store.getters.observationTemplates.length
    }

    get permissions() {
        return this.project
            ? this.$store.getters.project(this.project.id)?.attributes.permissions
            : this.getGroupPermissions(this.group)
    }

    get deviceTimeZone() {
        return this.guessTimezone()
    }

    InfiniteLoading: any = null

    // LAYOUT
    get observationLayout() {
        return this.$store.getters.observationLayout
    }

    set observationLayout(data: any) {
        this.$store.commit('observationLayout', data)
    }

    // // OBSERVATIONS
    observations: Array<any> = []
    infiniteID = +new Date()
    offset = 0
    limit = 10

    get observationFilter() {
        return this.$store.getters.observationFilter
    }

    get observationSort() {
        return this.$store.getters.observationSort
    }

    @Watch('observationFilter', { deep: true })
    @Watch('observationSort')
    @Watch('meMode')
    @Debounce(500)
    updateSearch(data: any) {
        this.offset = 0
        this.infiniteID += 1
        this.observations = []
    }

    async infiniteHandler($state: any) {
        const newParams = filterToParams(
            this.observationSort,
            this.observationFilter,
            'search',
            this.levelType,
            this.meMode,
            this.currentUser.id
        )

        let params = {
            ...newParams,
            ...(this.group && { 'filter[projectGroup]': this.group.id }),
            ...(this.project && { 'filter[project]': this.project.id }),
            'page[limit]': this.limit,
            'page[offset]': this.offset,
            includeCount: this.offset == 0 ? true : false,
        }

        if (!this.meMode) {
            params = {
                ...params,
                ...(this.observationFilter.status.length > 0 && {
                    'filter[status]': this.observationFilter.status.join(),
                }),
                ...(this.observationFilter.responsibleUser.length > 0 && {
                    'filter[responsibleUser]': this.observationFilter.responsibleUser.join(),
                }),
                ...(this.observationFilter.responsibleLeader.length > 0 && {
                    'filter[responsibleLeader]': this.observationFilter.responsibleLeader.join(),
                }),
            }
        } else {
            params = {
                ...params,
                ...(this.currentUser?.id && {
                    'filter[responsibleUserOrLeader]': this.currentUser.id,
                }),
                ...{ 'filter[status]': 'open,inProgress' },
            }
        }

        const { data } = await getRequest(
            this.getURL({
                path: `observations`,
                base: process.env.VUE_APP_OBSERVATIONS_BASE_URL,
                params: {
                    include:
                        'project,reporter,closedBy,mainAttachment,responsibleUser,responsibleLeader,category.parent,cause.parent,location.parent,locationDrawing,contractors,restrictedTo',
                    skipLinks: '',
                },
            }),
            null,
            params
        )
        if (this.offset == 0) this.totalObservations = data?.meta?.totalCount

        const observations = deserialise(data).data

        this.observations = [
            ...new Map(
                [...this.observations, ...observations].map((item) => [item['id'], item])
            ).values(),
        ]

        setTimeout(() => {
            if (observations.length == this.limit) {
                this.offset += this.limit
                $state.loaded()
            } else {
                $state.complete()
            }
        }, 100)
    }

    // TRACK ADDED
    get addedObservation() {
        return this.$store.getters.addedObservation
    }

    @Watch('addedObservation')
    async addObservation() {
        if (this.addedObservation) {
            this.observations.unshift(this.addedObservation)
            this.offset = this.offset + 1
            this.$store.commit('addedObservation', null)
        }
    }

    // TRACK DELETED OBSERVATION

    get deletedObservation() {
        return this.$store.getters.deletedObservation
    }

    @Watch('deletedObservation')
    async deleteItem() {
        if (this.deletedObservation) {
            let index = this.observations.findIndex((observation: any) => {
                return observation.id == this.deletedObservation.id
            })

            this.observations.splice(index, 1)
            this.offset = this.offset - 1
            this.$store.commit('deletedObservation', null)
        }
    }

    // TRACK UPDATED
    get updatedObservation() {
        return this.$store.getters.updatedObservation
    }

    @Watch('updatedObservation')
    async updateObservation() {
        if (this.updatedObservation) {
            const index = this.observations.findIndex((obs) => obs.id == this.updatedObservation.id)
            this.observations.splice(index, 1, this.updatedObservation)
            this.$store.commit('updatedObservation', null)
        }
    }

    get projectUserSetting() {
        return this.project?.relationships?.userSettings?.data?.id
            ? this.$store.getters.projectUserSetting(
                  this.project.relationships.userSettings.data.id
              )
            : null
    }

    get groupUserSetting() {
        return this.group?.relationships?.userSettings?.data.id
            ? this.$store.getters.groupUserSetting(this.group.relationships.userSettings.data.id)
            : null
    }

    get userSettings() {
        return this.project ? this.projectUserSetting : this.groupUserSetting
    }

    get observationProject() {
        return this.project ? this.$store.getters.observationProject(this.project.id) : null
    }

    get settings() {
        return this.observationProject?.relationships.settings.data?.id
            ? JSON.parse(
                  JSON.stringify(
                      this.$store.getters.observationProjectSetting(
                          this.observationProject.relationships.settings.data.id
                      )
                  )
              )
            : false
    }

    get observationColumnsArray() {
        return this.$store.getters.observationColumnsArray
    }

    get helpTextContent() {
        if (
            this.settings.attributes.helpText &&
            Object.keys(this.settings.attributes.helpText).length > 0
        ) {
            const [languageKey] = Object.keys(this.settings.attributes.helpText)
            return (
                this.settings.attributes.helpText[this.$store.getters.user.attributes.language] ||
                this.settings.attributes.helpText[languageKey]
            )
        }
        return null
    }

    @Watch('observationLayout')
    async updateObservationLayout() {
        if (this.userSettings) await this.updateProjectUserSettings()
        this.InfiniteLoading.stateChanger.reset()
    }

    @Watch('observationColumnsArray')
    @Debounce(3000)
    async updateObservationColumns() {
        if (this.userSettings) await this.updateProjectUserSettings()
    }

    async updateProjectUserSettings() {
        let json = this.userSettings.attributes.frontEndSettings
            ? JSON.parse(this.userSettings.attributes.frontEndSettings)
            : { observationModule: {} }

        if (!Object.prototype.hasOwnProperty.call(json, 'observationModule')) {
            json.observationModule = {}
        }

        json.observationModule.layout = this.observationLayout
        json.observationModule.columns = this.observationColumnsArray
        let data: any = {
            type: this.userSettings.type,
            id: this.userSettings.id,
            attributes: {
                frontEndSettings: JSON.stringify(json),
            },
        }
        if (this.project) {
            await this.patchProjectUserSettings(data).catch(() => {
                //
            })
        }
        if (this.group) {
            await this.patchtGroupUserSettings(data).catch(() => {
                //
            })
        }
    }

    async mounted() {
        let observer = new MutationObserver(() => {
            this.resizeAllMasonryItems()
        })

        if (document.getElementsByClassName('masonry')[0]) {
            observer.observe(document.getElementsByClassName('masonry')[0], {
                attributes: true,
                childList: true,
                characterData: true,
            })
            window.addEventListener('resize', this.resizeAllMasonryItems)
        }
        this.InfiniteLoading = this.$refs.InfiniteLoading
    }

    resizeAllMasonryItems() {
        let allItems = document.getElementsByClassName('masonry-brick')
        for (let i = 0; i < allItems.length; i++) {
            this.resizeMasonryItem(allItems[i])
        }
    }

    resizeMasonryItem(item: any) {
        let grid = document.getElementsByClassName('masonry')[0],
            rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap')),
            rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'))
        let rowSpan = Math.ceil(
            (item.querySelector('.masonry-item').getBoundingClientRect().height + rowGap) /
                (rowHeight + rowGap)
        )
        item.style.gridRowEnd = 'span ' + rowSpan
    }

    @Watch('observationLayout')
    cardLoaded() {
        setTimeout(this.resizeAllMasonryItems, 100)
    }
}
